import { APP_INITIALIZER, Injector } from '@angular/core';
import { catchError, firstValueFrom } from 'rxjs';
import {
  appInjector,
  AppTitleService,
  AuthenticationService,
  BaseApiPermissionsService,
  BaseEnvironmentService,
  PermissionManagerConfig,
  PermissionsService
} from '@pl/pl-lib/common';
import { TranslateService } from '@ngx-translate/core';
import { IHydrateArgs } from '@pl/permission-manager';
import { ErrorsService } from '@pl/pl-lib/errors';
import { CurrentIdentityProvider } from '@pl/authorisation-client';
import { AppSettingService } from './core/services/appsetting';

export const appInitialiserFactory = (
  environmentService: BaseEnvironmentService,
  authenticationService: AuthenticationService,
  permissionsService: PermissionsService,
  sgPermissionsService: BaseApiPermissionsService,
  appSettingService: AppSettingService,
  permissionManagerConfig: PermissionManagerConfig,
  translateService: TranslateService,
  injector: Injector,
  appTitleService: AppTitleService,
  errorService: ErrorsService,
  currentIdentityProvider: CurrentIdentityProvider
) => {
  return async () => {
    appInjector(injector);

    translateService.setDefaultLang('en');
    if (
      !(await firstValueFrom(
        translateService
          .use('en')
          .pipe(
            catchError(
              errorService.handleObservableCatchError(
                'i18n',
                'en',
                'Error while initialising translation service.'
              )
            )
          )
      ))
    ) {
      return;
    }

    if (!(await environmentService.loadAndHandleErrors())) {
      return;
    }

    permissionManagerConfig.placUrl =
      permissionManagerConfig.placUrl || environmentService.placApiHostUrl;
    permissionManagerConfig.placApplicationId =
      permissionManagerConfig.placApplicationId ||
      environmentService.placApplicationId;
    permissionManagerConfig.placApplicationName =
      permissionManagerConfig.placApplicationName ||
      environmentService.placApplicationName;
    permissionManagerConfig.ttl = 900000;

    permissionManagerConfig.dynamicResolversGetter = {
      get: (name) => {
        return Promise.resolve({
          resolve: (attributeValue: string, parameters: string) =>
            firstValueFrom(
              sgPermissionsService
                .getDynamicResolverValue(name)
                .pipe(
                  catchError(
                    errorService.handleObservableCatchError(
                      'PermissionService',
                      '3'
                    )
                  )
                )
            )
        });
      }
    };
    permissionManagerConfig.missingIncludesHydraterGetter = {
      hydrate: async (args: IHydrateArgs) => {
        return firstValueFrom(sgPermissionsService.hydrate(args));
      }
    };
    permissionManagerConfig.setSubjectNameToDbModelMapFunction = () => {
      return firstValueFrom(
        sgPermissionsService
          .getSubjectToEntityTypeMap()
          .pipe(
            catchError(
              errorService.handleObservableCatchError('PermissionService', '2')
            )
          )
      );
    };

    if (!(await appSettingService.loadAndHandleErrors())) {
      return;
    }

    if (
      window.location.pathname.toString().indexOf('/login') === -1 && (
      window.location.toString() === environmentService.authConfig.postLogoutRedirectUri ||
      window.location.pathname.toString().indexOf('/app') !== 0)
    ) {
      // If it is, exit the initialization to display the logout page without re-authenticating
      return;
    }

    if (!(await authenticationService.init(permissionsService))) {
      return;
    }

    try {
      currentIdentityProvider.configure(
        environmentService.placApplicationName ||
          (environmentService.placApplicationId as string)
      );
    } catch (e) {
      return await errorService.handleException(e, 'Authentication', '2');
    }

    try {
      appTitleService.init();
    } catch (e) {
      return await errorService.handleException(e, 'TitleService', '1');
    }
    return true;
  };
};

export const AppInitialiser = {
  provide: APP_INITIALIZER,
  useFactory: appInitialiserFactory,
  deps: [
    BaseEnvironmentService,
    AuthenticationService,
    PermissionsService,
    BaseApiPermissionsService,
    AppSettingService,
    PermissionManagerConfig,
    TranslateService,
    Injector,
    AppTitleService,
    ErrorsService,
    CurrentIdentityProvider
  ],
  multi: true
};
